// src/components/Footer/Footer.tsx
import React from 'react';
import { Link } from 'react-router';
import './Footer.css';
import { AssetUrls } from 'config/assets';

/**
 * Grouped footer (Asana 1219902842669115). A new link is one line in the group
 * it belongs to; the grid gives it a column instead of wrapping a row. Internal
 * links are router Links, external ones anchors, so crawlers follow both.
 */
export interface FooterLink {
  label: string;
  /** Internal route (router Link). */
  to?: string;
  /** External or protocol link (plain anchor). */
  href?: string;
  /** Optional leading icon (the Contact mail glyph). */
  icon?: { src: string; alt: string; width: number; height: number };
}

export interface FooterLinkGroup {
  title: string;
  links: FooterLink[];
}

export const FOOTER_TAGLINE = 'Trade Magic cards with players. Peer-to-peer trade, perfected.';

export const FOOTER_LINK_GROUPS: FooterLinkGroup[] = [
  {
    title: 'Explore',
    links: [
      { label: 'Search', to: '/search' },
      { label: 'Sets', to: '/sets' },
      { label: 'Formats', to: '/formats' },
      { label: 'Compare', to: '/compare' },
      // TradeMagic Play (Asana 1220000146037011, Decision 28b)
      { label: 'TradeMagic Play', to: '/play' },
    ],
  },
  {
    title: 'Help',
    links: [
      { label: 'About', to: '/about' },
      { label: 'FAQ', to: '/faq' },
      {
        label: 'Contact',
        href: 'mailto:support@trademagic.gg',
        icon: { src: AssetUrls.icons.footer.contact, alt: '', width: 14, height: 11 },
      },
      { label: 'Articles', to: '/articles' },
    ],
  },
  {
    title: 'Legal',
    links: [
      { label: 'Terms of Service', to: '/terms' },
      { label: 'Privacy Policy', to: '/privacy' },
    ],
  },
];

const SOCIAL_LINKS = [
  {
    name: 'Twitter',
    url: 'https://x.com/trademagic_gg',
    icon: AssetUrls.icons.social.footer.twitter,
  },
  {
    name: 'Bluesky',
    url: 'https://bsky.app/profile/trademagic.bsky.social',
    icon: AssetUrls.icons.social.footer.bluesky,
  },
  {
    name: 'Reddit',
    url: 'https://www.reddit.com/r/TradeMagic',
    icon: AssetUrls.icons.social.footer.reddit,
  },
];

const FooterLinkItem: React.FC<{ link: FooterLink }> = ({ link }) => {
  const content = (
    <>
      {link.icon && (
        <img
          src={link.icon.src}
          alt={link.icon.alt}
          width={link.icon.width}
          height={link.icon.height}
          className="footer-link-icon"
        />
      )}
      {link.label}
    </>
  );
  if (link.to) {
    return (
      <Link to={link.to} className="footer-link">
        {content}
      </Link>
    );
  }
  return (
    <a href={link.href} className="footer-link">
      {content}
    </a>
  );
};

const Footer = () => (
  <footer className="site-footer">
    <div className="footer-inner">
      <div className="footer-grid">
        <div className="footer-brand">
          <div className="footer-brand-row">
            <img
              src={AssetUrls.logos.arevakhachIcon}
              alt="TradeMagic logo"
              className="footer-logo"
            />
            <span className="footer-wordmark">trademagic.gg</span>
          </div>
          <p className="footer-tagline">{FOOTER_TAGLINE}</p>
          <div className="footer-social">
            {SOCIAL_LINKS.map((link) => (
              <a
                key={link.name}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Visit TradeMagic on ${link.name}`}
                className="social-link"
              >
                <img src={link.icon} alt={`${link.name} Icon`} width="25" height="25" />
              </a>
            ))}
          </div>
        </div>

        {FOOTER_LINK_GROUPS.map((group) => (
          <nav key={group.title} className="footer-col" aria-label={group.title}>
            <h3 className="footer-col-title">{group.title}</h3>
            <ul className="footer-col-list">
              {group.links.map((link) => (
                <li key={link.label}>
                  <FooterLinkItem link={link} />
                </li>
              ))}
            </ul>
          </nav>
        ))}
      </div>

      <hr className="footer-divider" />

      <div className="footer-legal" data-nosnippet>
        <p>
          Portions of <strong>TradeMagic.gg</strong> are unofficial Fan Content permitted under the Wizards of the Coast Fan Content Policy.
          The literal and graphical information presented on this site about Magic: The Gathering, including card images and mana symbols,
          is copyright Wizards of the Coast, LLC. <strong>TradeMagic.gg</strong> is not produced by or endorsed by Wizards of the Coast.
        </p>
        <p>
          Card data and pricing from <a href="https://scryfall.com" target="_blank" rel="noopener noreferrer">Scryfall</a>.
          Prices represent daily estimates and/or market values. Absolutely no guarantee is made for any price information.
        </p>
        <p className="footer-copy">
          © {new Date().getFullYear()} <strong>TradeMagic, Inc.</strong>
        </p>
      </div>
    </div>

    <img
      src={AssetUrls.icons.footer.cards}
      alt="Footer Cards Decoration"
      className="footer-decor"
    />
  </footer>
);

export default Footer;
